import {useEffect, useState, useCallback} from "react";
import toast from "react-hot-toast";
import {useNavigate, useParams} from "react-router-dom";
import {fetchWithAuth} from "../libs/apiUtils";
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import {Meta} from "../types/global";
import {PencilIcon, TrashIcon, PlusIcon, TableCellsIcon, LockClosedIcon, StarIcon} from "@heroicons/react/24/outline";
import DeleteCategoryModal from "../components/category/DeleteCategoryModal";
import EditCategoryModal from "../components/category/EditCategoryModal";
import CreateCategoryModal from "../components/category/CreateCategoryModal";
import Pagination from "../components/Pagination";

interface CategoryData {
  id: string
  name: string
  createdByName?: string
}

export default function Category() {
  const { groupId } = useParams()
  const [groupName, setGroupName] = useState<string | null>(null)
  const [groupIsDefault, setGroupIsDefault] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [categories, setCategories] = useState<CategoryData[]>([])
  const [meta, setMeta] = useState<Meta | null>(null)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(10)
  const navigate = useNavigate()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [toBeDeletedCategory, setToBeDeletedCategory] = useState<{id?: string, name?: string}>({})

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [toBeEditedCategory, setToBeEditedCategory] = useState<{id?: string, groupId?: string, name?: string}>({})

  const userRoleSlug = localStorage.getItem('user_role_slug')
  const isSuperAdmin = userRoleSlug === 'super_admin'
  const isConsumer = userRoleSlug === 'consumer'
  const isAdmin = userRoleSlug === 'admin'

  // Can manage categories only if:
  // - super_admin always can
  // - admin can only if the group is NOT a default group
  const canManage = isSuperAdmin || (isAdmin && !groupIsDefault)

  function navigateBack() {
    navigate('/data/group')
  }

  const loadCategories = useCallback(async (currentPage: number = 1, gId: string) => {
    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: currentPage, perPage, groupId: gId }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Error fetching category data')
        return
      }

      setCategories(data.data || [])
      setMeta(data.meta)
    } catch (err) {
      toast.error('Network error, check your connection')
    } finally {
      setLoading(false)
    }
  }, [perPage])

  async function handleDeleteCategory(id: string) {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/category/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || data[0]?.message || 'Error deleting category')
        return
      }

      toast.success('Category deleted successfully')
      loadCategories(page, groupId!)
    } catch (err) {
      toast.error('Network error, check your connection')
    }
  }

  useEffect(() => {
    if (groupId) loadCategories(page, groupId)
  }, [page, groupId, loadCategories])

  useEffect(() => {
    async function validateGroup() {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/group-detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ groupId }),
        })

        if (!res.ok) throw new Error('Invalid group')

        const data = await res.json()
        setGroupName(data.data.name)
        setGroupIsDefault(data.data.isDefault ?? false)
      } catch (err) {
        toast.error('Group not found')
        navigate('/data/group')
      } finally {
        setLoading(false)
      }
    }

    if (groupId) validateGroup()
  }, [groupId, navigate])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={navigateBack} 
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors text-neutral-600"
            title="Kembali ke Daftar Grup"
          >
            <ArrowLeftIcon className="size-6" />
          </button>
          <div>
            <nav className="flex text-sm text-neutral-500 mb-1">
              <button onClick={() => navigate('/data/group')} className="hover:text-primary-600 transition-colors">
                {userRoleSlug === 'consumer' ? 'Akses Data' : 'Manajemen Grup'}
              </button>
              <span className="mx-2">/</span>
              <span className="text-neutral-900 font-medium">{groupName}</span>
            </nav>
            <h2 className="text-3xl font-extrabold text-neutral-900 tracking-tight flex items-center gap-3">
              Grup: <span className="text-primary-600">{groupName || '...'}</span>
              {groupIsDefault && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                  <StarIcon className="size-3.5" /> Grup Bawaan
                </span>
              )}
            </h2>
          </div>
        </div>
        
        {canManage && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-lg shadow-primary-200 transition-all active:scale-95"
          >
            <PlusIcon className="size-5" />
            Tambah Kategori
          </button>
        )}
      </div>

      {/* Read-only banner for admins in default groups */}
      {groupIsDefault && !isSuperAdmin && !isConsumer && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-6 text-sm">
          <LockClosedIcon className="size-5 shrink-0 text-amber-600" />
          <span>Ini adalah <strong>Grup Bawaan</strong> yang dikelola oleh Super Admin. Anda dapat melihat dan mengisi data tabel di dalamnya, tetapi tidak dapat menambah, mengubah, atau menghapus kategori.</span>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 overflow-hidden">
        { loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-neutral-500 font-medium">Memuat data...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-20 text-center">
            <div className="bg-primary-50 size-16 rounded-full flex items-center justify-center mx-auto mb-4 text-primary-600">
              {canManage ? <PlusIcon className="size-8" /> : <TableCellsIcon className="size-8" />}
            </div>
            <h3 className="text-lg font-bold text-neutral-800">
              {canManage ? 'Belum Ada Kategori' : 'Belum Ada Kategori'}
            </h3>
            <p className="text-neutral-500 max-w-xs mx-auto mt-2">
              {canManage
                ? 'Silakan klik tombol "Tambah Kategori" untuk memulai pengelolaan data.'
                : 'Belum ada kategori yang ditambahkan di grup ini.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-50/50">
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-20">No</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Nama Kategori</th>
                  <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider">Dibuat Oleh</th>
                  {canManage &&
                    <th className="px-6 py-4 text-sm font-bold text-neutral-600 uppercase tracking-wider w-32 text-center">Aksi</th>
                  }
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {categories.map((category, index) => (
                  <tr 
                    key={category.id} 
                    className="group hover:bg-primary-50/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-neutral-500">
                      {(meta?.perPage || 20) * (page - 1) + index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        className="text-neutral-900 font-semibold hover:text-primary-600 transition-colors text-left block w-full"
                        onClick={() => navigate(`/data/${groupId}/${category.id}/table`)}
                      >
                        {category.name}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-neutral-500">{category.createdByName || '-'}</span>
                    </td>
                    {canManage && (
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            title="Edit Kategori"
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeEditedCategory({id: category.id, groupId: groupId, name: category.name})
                              setIsEditModalOpen(true)
                            }}
                          >
                            <PencilIcon className="size-5" />
                          </button>
                          <button
                            title="Hapus Kategori"
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            onClick={() => {
                              setToBeDeletedCategory({id: category.id, name: category.name})
                              setIsDeleteModalOpen(true)
                            }}
                          >
                            <TrashIcon className="size-5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && (
          <div className="px-6 py-4 bg-neutral-50/50 border-t border-neutral-100">
            <Pagination
              currentPage={page}
              lastPage={meta.lastPage}
              total={meta.total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(newPerPage) => {
                setPerPage(newPerPage)
                setPage(1)
              }}
            />
          </div>
        )}
      </div>

      <CreateCategoryModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        groupId={groupId!}
        onSuccess={() => loadCategories(page, groupId!)}
      />
      <DeleteCategoryModal 
        isOpen={isDeleteModalOpen} 
        setIsOpen={setIsDeleteModalOpen} 
        name={toBeDeletedCategory.name ?? ''}
        onSubmit={() => handleDeleteCategory(toBeDeletedCategory.id ?? '')}
      />
      <EditCategoryModal 
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        category={toBeEditedCategory}
        onSuccess={() => loadCategories(page, groupId!)}
      />
    </div>
  )
}
